import { defineModel } from "../define-model";

export type SocialPlatform = "instagram" | "tiktok" | "facebook" | "threads" | "x";

export type AccountHealth = {
	status: "healthy" | "warning" | "error";
	canPost: boolean;
	needsReconnect: boolean;
	issues: string[];
	tokenExpiresAt?: string | null;
	checkedAt: string;
};

/** `metadata.import`: progress of the account's latest post import. */
export type ImportState = {
	status: "running" | "done" | "error";
	mode: "full" | "incremental";
	/** Page within the current date window (Zernio reads one year at a time). */
	page: number;
	pages: number | null;
	windowTo: string;
	imported: number;
	startedAt: string;
	heartbeatAt: string;
	finishedAt: string | null;
	completedAt: string | null;
	error: string | null;
	retryAt: string | null;
};

/** `metadata.sync`: the latest content sync (import, metrics, comments, followers). */
export type AccountSyncState = {
	status: "running" | "done" | "error";
	source: "schedule" | "manual" | "connect";
	startedAt: string;
	finishedAt: string | null;
	error: string | null;
	pending: ("import" | "metrics" | "comments" | "followers")[];
};

export type SocialAccount = {
	id: string;
	workspaceId: string;
	projectId: string;
	platform: SocialPlatform;
	zernioAccountId: string;
	zernioProfileId: string | null;
	handle: string;
	displayName: string | null;
	avatarUrl: string | null;
	profileUrl: string | null;
	status: "connected" | "needs_reauth" | "disconnected" | "error";
	metadata: {
		health?: AccountHealth;
		followers?: number;
		followerStats?: { checkedAt?: string; error?: string | null; unavailableUntil?: string | null };
		import?: ImportState;
		sync?: AccountSyncState;
		/** Pre-`import` fields on accounts imported before progress tracking. */
		postsImportedAt?: string;
		importedPosts?: number;
		importError?: string;
	} & Record<string, unknown>;
	connectedById: string | null;
	connectedAt: string;
	lastSyncedAt: string | null;
	createdAt: string;
	updatedAt: string;
};

declare module "../types" {
	interface SyncModels {
		socialAccount: SocialAccount;
	}
}

export const model = defineModel({
	name: "socialAccount",
	load: "eager",
	indexes: ["projectId"],
	sort: (a, b) => a.platform.localeCompare(b.platform) || a.handle.localeCompare(b.handle),
});
